import React, { useState } from 'react';
import { forNamespace } from 'app/common/lib/util/i18n';
import {
  Button,
  ButtonSizes,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import {
  useTeamfulCollaborationAddBoardsQuery,
  useTeamfulCollaborationMoveBoardToOrgMutation,
} from '@trello/graphql/generated';
import { Checkbox } from '@trello/nachos';
import styles from './TeamfulCollaborationAddBoards.less';

const Alert = require('app/scripts/views/lib/alerts');

const format = forNamespace('teamful collaboration prompt');

interface TeamfulCollaborationAddBoardsProps {
  onContinue: () => void;
  skipToSuccess: () => void;
  orgId: string;
}

export const TeamfulCollaborationAddBoards: React.FunctionComponent<TeamfulCollaborationAddBoardsProps> = ({
  skipToSuccess,
  onContinue,
  orgId,
}) => {
  const [
    moveBoardToOrgMutation,
  ] = useTeamfulCollaborationMoveBoardToOrgMutation();
  const initialState: Set<string> = new Set();
  const boardEntries = [];
  const maxBoardsShown = 4;
  const [selectedBoards, setSelected] = useState(initialState);
  const { data, loading } = useTeamfulCollaborationAddBoardsQuery();
  const getOpenBoards = () => {
    if (loading) {
      return [];
    }

    const member = data?.member;
    if (!member) {
      return [];
    }

    const myId = member.id;
    const openBoards = [];

    for (let i = 0; i < member.boards.length; i++) {
      const board = member.boards[i];
      if (board.enterpriseOwned || board.idOrganization !== null) {
        continue;
      }

      const memberships = board.memberships;
      let memberCount = 0;
      let amIAdmin = false;

      for (let j = 0; j < memberships.length; j++) {
        const { idMember, memberType, unconfirmed } = memberships[j];

        if (!amIAdmin && idMember === myId && memberType === 'admin') {
          amIAdmin = true;
        }

        if (!unconfirmed) {
          memberCount += 1;
        }
      }

      if (memberCount >= 3 && amIAdmin) {
        openBoards.push(board);
      }
    }

    return openBoards;
  };

  if (loading) {
    // TODO: add a spinner
    return <div>Loading</div>;
  }

  const openBoards = getOpenBoards();
  if (openBoards.length <= 0) {
    skipToSuccess();
  }

  const entryLimit = Math.min(openBoards.length, maxBoardsShown);
  for (let i = 0; i < entryLimit; i++) {
    const board = openBoards[i];
    const backgroundColor = board?.prefs?.backgroundColor
      ? board.prefs.backgroundColor
      : undefined;
    const backgroundImage = board?.prefs?.backgroundImage
      ? board.prefs.backgroundImage
      : undefined;

    const entry = (
      <BoardListItem
        onSelect={() => {
          if (selectedBoards.has(board.id)) {
            selectedBoards.delete(board.id);
          } else {
            selectedBoards.add(board.id);
          }

          setSelected(new Set(selectedBoards));
        }}
        selected={selectedBoards.has(board.id)}
        name={board.name}
        backgroundImage={backgroundImage}
        backgroundColor={backgroundColor}
        key={board.id}
      />
    );
    boardEntries.push(entry);
  }

  const onSubmit = async () => {
    const selectedBoardsArray: Array<string> = Array.from(selectedBoards);

    try {
      const mutations = [];

      for (let i = 0; i < selectedBoardsArray.length; i++) {
        const resp = moveBoardToOrgMutation({
          variables: {
            boardId: selectedBoardsArray[i],
            orgId,
          },
        });
        mutations.push(resp);
      }

      await Promise.all(mutations);
      onContinue();
    } catch (e) {
      // TODO: This will need to change once error copy is finalized
      Alert.showLiteralText(e.message, 'error', 'alert', 5000);
    }
  };

  return (
    <div className={styles.addBoards}>
      {/* TODO: FINALIZE COPY */}
      <p className={styles.promptTitle}>Add Boards to this Team</p>
      <p className={styles.promptDesc}>
        {/* TODO: FINALIZE COPY */}
        Collaborators will be able to see, edit, and add more Boards to this
        Team.
      </p>
      {boardEntries}
      <p className={styles.promptFooter}>
        <i>{format(['add boards form', 'move later'])}</i>
      </p>
      <Button
        buttonType={ButtonTypes.PRIMARY}
        className={styles.addBoardsButton}
        onClick={onSubmit}
        size={ButtonSizes.FULLWIDTH}
      >
        {format(['add boards form', 'next'])}
      </Button>
    </div>
  );
};

interface BoardLineEntryProps {
  name: string;
  selected: boolean;
  onSelect: () => void;
  backgroundImage: string | undefined;
  backgroundColor: string | undefined;
}

const BoardListItem: React.FunctionComponent<BoardLineEntryProps> = ({
  name,
  selected,
  onSelect,
  backgroundImage,
  backgroundColor,
}) => {
  const tileStyle = {
    backgroundColor: backgroundColor,
  };

  return (
    <div
      role="checkbox"
      aria-checked={selected}
      className={styles.boardListEntry}
    >
      <div className={styles.backgroundTile} style={tileStyle}>
        {backgroundImage && (
          <img alt="board-background" src={backgroundImage} />
        )}
      </div>
      <p>{name}</p>
      <div className={styles.checkbox}>
        <Checkbox onChange={onSelect} isFullWidth isChecked={selected} />
      </div>
    </div>
  );
};
